/**
 * Server-minted credentials for the Translator rail (AC-TRX-1).
 *
 * The rail talks to two vendors directly from the browser — Google Vertex AI
 * (Gemini Live Translate) and ElevenLabs Scribe v2 Realtime — but NEITHER
 * vendor's long-lived key may ever reach this bundle. care-management-gql holds
 * both and mints short-lived material per session:
 *
 *   - `translatorGeminiToken` → an OAuth access token scoped to Vertex AI,
 *     minted from the server's service account.
 *   - `translatorElevenLabsSignedUrl` → a pre-signed WebSocket URL; the
 *     `xi-api-key` stays server-side.
 *
 * Same posture as `src/clinical/gqlLlmClient.ts` (the Azure OpenAI proxy):
 * server holds the secret, the browser holds only a short-lived derivative.
 *
 * Two rules this module exists to enforce:
 *
 * 1. **Never cache.** Both getters run `fetchPolicy: "no-cache"`. A cached
 *    credential is a credential served past its expiry (and one written into
 *    Apollo's normalized store, where it would outlive the session).
 * 2. **Fail closed, loudly.** Any error — server not configured, network,
 *    null field, blank value — becomes a thrown Error whose message names the
 *    translator, so the component's `onError` can put the rail into its
 *    read-only state instead of opening a socket with `undefined` credentials.
 */
import { ApolloClient, gql } from "@apollo/client";

/**
 * Must match care-management-gql's field exactly (Stream B, Task B1):
 * `translatorGeminiToken: TranslatorGeminiToken!` with
 * `TranslatorGeminiToken { token: String!, expiresAt: String! }`.
 *
 * `expiresAt` is selected but not currently used here: the component asks for a
 * fresh token per session, so there is nothing to expire-check yet. It is kept
 * in the selection set so the field stays exercised end-to-end and a future
 * refresh-ahead policy needs no schema/contract change.
 */
export const TRANSLATOR_GEMINI_TOKEN = gql`
  query TranslatorGeminiToken {
    translatorGeminiToken {
      token
      expiresAt
    }
  }
`;

/**
 * Must match care-management-gql's field exactly (Stream B, Task B2):
 * `translatorElevenLabsSignedUrl: String!`.
 */
export const TRANSLATOR_ELEVENLABS_SIGNED_URL = gql`
  query TranslatorElevenLabsSignedUrl {
    translatorElevenLabsSignedUrl
  }
`;

export interface TranslatorCredentials {
  /** Short-lived Vertex-AI-scoped OAuth token for Gemini Live Translate. */
  getGeminiToken: () => Promise<string>;
  /** Short-lived signed WSS URL for ElevenLabs Scribe v2 Realtime. */
  getElevenLabsSignedUrl: () => Promise<string>;
}

/**
 * Wraps a credential failure in a translator-named Error.
 *
 * The vendor/server message is preserved (appended, and as `cause`) because it
 * is the actionable half — "not configured" vs. a 401 vs. a network drop are
 * very different operational answers — but the prefix guarantees the surfaced
 * toast/telemetry always says which feature failed.
 */
function credentialError(what: string, cause: unknown): Error {
  const detail =
    cause instanceof Error ? cause.message : cause ? String(cause) : "";
  const err = new Error(
    `Translator could not obtain ${what}.${detail ? ` ${detail}` : ""}`,
  );
  // Keep the original GraphQL/network error for App Insights. Assigned rather
  // than passed as `new Error(msg, { cause })` — this package's tsconfig lib
  // predates the ES2022 Error `options` overload, so the two-arg form does not
  // typecheck here even though every supported runtime honours it.
  (err as Error & { cause?: unknown }).cause = cause;
  return err;
}

/**
 * Builds the two credential getters the `Translator` component takes.
 *
 * @param client the page's Apollo client (already carrying the Entra auth
 *   link). Both fields are gated by the normal authenticated session — no PP
 *   token is involved — exactly like `llmChatCompletion`.
 */
export function createTranslatorCredentials(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: ApolloClient<any>,
): TranslatorCredentials {
  return {
    async getGeminiToken() {
      let token: string | undefined;
      try {
        const { data } = await client.query<{
          translatorGeminiToken: { token: string; expiresAt: string } | null;
        }>({
          query: TRANSLATOR_GEMINI_TOKEN,
          fetchPolicy: "no-cache",
        });
        token = data?.translatorGeminiToken?.token;
      } catch (cause) {
        throw credentialError("a Gemini access token", cause);
      }

      // A null field / blank token means the server is not configured for
      // Vertex AI. Fail closed rather than handing "" to the Live API.
      if (!token) {
        throw credentialError(
          "a Gemini access token",
          "The server returned no token — Vertex AI may not be configured for this environment.",
        );
      }
      return token;
    },

    async getElevenLabsSignedUrl() {
      let url: string | undefined;
      try {
        const { data } = await client.query<{
          translatorElevenLabsSignedUrl: string | null;
        }>({
          query: TRANSLATOR_ELEVENLABS_SIGNED_URL,
          fetchPolicy: "no-cache",
        });
        url = data?.translatorElevenLabsSignedUrl ?? undefined;
      } catch (cause) {
        throw credentialError("an ElevenLabs signed URL", cause);
      }

      if (!url) {
        throw credentialError(
          "an ElevenLabs signed URL",
          "The server returned no URL — ElevenLabs may not be configured for this environment.",
        );
      }
      return url;
    },
  };
}
